import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosClient from "../../../components/api/axiosClient";
import LoadContentSpinner from "../../../components/Utils/LoadContentSpinner";
import { formatNumber } from "../../../components/Utils/UtilsNumbersFormat";

/**
 * API attendue (à aligner avec le back-office) :
 * GET /dashboard/organizer/scanner/affluence-statistics
 * Réponse typique : { data: [ { id, event_title, event_code, default_date, affiche?, physical: { total, scanned }, digital: { total, scanned } } ] }
 * Les clés peuvent varier : voir normalizeChannel() / normalizeEventRow().
 */
const AFFLUENCE_ENDPOINT = "/dashboard/events/attendance/scan-stats";
const POLL_MS = 20000;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function normalizeChannel(raw) {
  if (raw == null || typeof raw !== "object") {
    return { total: 0, scanned: 0, notScanned: 0 };
  }
  const total = num(
    raw.total ?? raw.total_tickets ?? raw.count ?? raw.tickets ?? raw.all,
  );
  let scanned = num(
    raw.scanned ??
      raw.scanned_count ??
      raw.used ??
      raw.entrees ??
      raw.validated,
  );
  if (scanned > total && total > 0) scanned = total;
  const notScanned = Math.max(0, total - scanned);
  return { total, scanned, notScanned };
}

function normalizeEventRow(raw) {
  const physical = normalizeChannel(
    raw.physical ??
      raw.physique ??
      raw.ticket_physique ??
      raw.print ??
      raw.scan_physical,
  );
  const digital = normalizeChannel(
    raw.digital ??
      raw.numerique ??
      raw.e_ticket ??
      raw.mobile ??
      raw.scan_digital,
  );

  return {
    id: raw.id,
    event_title: raw.event_title ?? raw.titre ?? "Événement",
    event_code: raw.event_code ?? raw.code ?? "—",
    default_date: raw.default_date ?? raw.date_range ?? "",
    affiche: raw.affiche,
    physical,
    digital,
  };
}

function pct(scanned, total) {
  if (!total) return 0;
  return Math.round((scanned / total) * 1000) / 10;
}

/** Anneau de progression (taux global) — id SVG unique pour dégradés */
function RateRing({ value, label, sub }) {
  const uid = useId();
  const gradId = `ring-grad-${uid.replace(/:/g, "")}`;
  const r = 42;
  const stroke = 6;
  const c = 2 * Math.PI * r;
  const clamped = Math.min(100, Math.max(0, value));
  const offset = c - (clamped / 100) * c;

  return (
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <div className="relative shrink-0">
        <svg
          width={120}
          height={120}
          viewBox="0 0 100 100"
          className="-rotate-90 drop-shadow-lg"
          aria-hidden
        >
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#34d399" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
          </defs>
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            className="text-gray-200 dark:text-white/15"
          />
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={`url(#${gradId})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            strokeDashoffset={offset}
            className="transition-[stroke-dashoffset] duration-700 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-gray-900 dark:text-white">
          <span className="text-3xl font-black tabular-nums tracking-tight">
            {Number.isFinite(value) ? `${clamped}%` : "—"}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-white/70 font-semibold">
            {label}
          </span>
        </div>
      </div>
      {sub && (
        <p className="text-sm text-gray-600 dark:text-gray-300 text-center sm:text-left max-w-xs leading-relaxed">
          {sub}
        </p>
      )}
    </div>
  );
}

/** Bloc stats par canal — style épuré (fond clair, peu de couleur) */
function ChannelBlock({ title, subtitle, icon, variant = "physical", data }) {
  const { total, scanned, notScanned } = data;
  const p = pct(scanned, total);

  const accent =
    variant === "digital"
      ? "border-l-teal-400/50 dark:border-l-teal-500/40"
      : "border-l-slate-300 dark:border-l-slate-600";

  const iconBg =
    variant === "digital"
      ? "bg-teal-50 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300"
      : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";

  return (
    <div
      className={`rounded-2xl border border-gray-200/90 dark:border-gray-700/80 bg-white dark:bg-gray-900/60 pl-4 pr-5 py-5 shadow-sm transition-shadow hover:shadow-md ${accent} border-l-[3px]`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconBg}`}
          >
            {icon}
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 tracking-tight truncate">
              {title}
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {subtitle}
            </p>
          </div>
        </div>
        <span className="shrink-0 text-xs font-semibold px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
          {total ? `${p}%` : "—"}
        </span>
      </div>

      <div className="flex items-baseline gap-1.5 mb-3">
        <span className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums tracking-tight">
          {formatNumber(scanned)}
        </span>
        <span className="text-sm text-gray-400 dark:text-gray-500 font-medium">
          / {formatNumber(total)}
        </span>
      </div>

      <div
        className="flex h-2 w-full rounded-full overflow-hidden mb-3 bg-gray-100 dark:bg-gray-800"
        role="img"
        aria-label={`Progression : ${formatNumber(scanned)} scannés sur ${formatNumber(total)}`}
      >
        {total > 0 ? (
          <>
            <div
              className="h-full bg-emerald-500/85 dark:bg-emerald-500 shrink-0 transition-[width] duration-500 ease-out"
              style={{ width: `${Math.min(100, p)}%` }}
            />
            <div
              className="h-full bg-amber-300/90 dark:bg-amber-500/70 shrink-0 transition-[width] duration-500 ease-out"
              style={{ width: `${Math.max(0, 100 - Math.min(100, p))}%` }}
            />
          </>
        ) : (
          <div className="h-full w-full bg-gray-200 dark:bg-gray-700" />
        )}
      </div>

      <dl className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/80 px-3 py-2 border border-gray-100 dark:border-gray-700/80">
          <dt className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Scannés
          </dt>
          <dd className="mt-1 font-semibold text-gray-900 dark:text-gray-100 tabular-nums text-sm">
            {formatNumber(scanned)}
          </dd>
        </div>
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800/80 px-3 py-2 border border-gray-100 dark:border-gray-700/80">
          <dt className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400 dark:bg-amber-500/80" />
            Non scannés
          </dt>
          <dd className="mt-1 font-semibold text-gray-900 dark:text-gray-100 tabular-nums text-sm">
            {formatNumber(notScanned)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

export default function AffluenceStatisticsPage() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [liveRefresh, setLiveRefresh] = useState(true);

  const fetchAffluence = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) {
      setError(null);
    }
    if (silent) {
      setRefreshing(true);
    }
    try {
      const { data: payload } = await axiosClient.get(AFFLUENCE_ENDPOINT);
      if (payload?.noContent) {
        setRows([]);
        setLastUpdated(new Date());
        return;
      }
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      setRows(list.map(normalizeEventRow));
      setLastUpdated(new Date());
      setError(null);
    } catch (e) {
      if (!silent) {
        if (e.response?.data) {
          setError(e.response.data);
        } else {
          setError({
            message: "Impossible de charger les statistiques d'affluence.",
          });
        }
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
      if (silent) {
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchAffluence({ silent: false });
  }, [fetchAffluence]);

  useEffect(() => {
    if (!liveRefresh) return undefined;
    const id = setInterval(() => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      )
        return;
      fetchAffluence({ silent: true });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [liveRefresh, fetchAffluence]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, ev) => {
        acc.physicalTotal += ev.physical.total;
        acc.physicalScanned += ev.physical.scanned;
        acc.digitalTotal += ev.digital.total;
        acc.digitalScanned += ev.digital.scanned;
        return acc;
      },
      {
        physicalTotal: 0,
        physicalScanned: 0,
        digitalTotal: 0,
        digitalScanned: 0,
      },
    );
  }, [rows]);

  const globalTotalTickets = totals.physicalTotal + totals.digitalTotal;
  const globalScanned = totals.physicalScanned + totals.digitalScanned;
  const globalPending = Math.max(0, globalTotalTickets - globalScanned);
  const globalRate = pct(globalScanned, globalTotalTickets);

  if (loading) {
    return <LoadContentSpinner />;
  }

  if (error && !rows.length) {
    return (
        <div className="w-full h-full flex justify-center items-center cweej">
                <div className="cydwr clbq0">
                    <div className="inline-flex items-center justify-center rounded-full c1qph c3x05 c7rlo ce36h cbcgo cdiog c3nk1 crzrx">
                        <svg className="cbm9w ctt6r cye3x" viewBox="0 0 20 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                            <path
                                className="text-gray-500 co1wq"
                                d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z"
                                fill="currentColor"
                            />
                            <path
                                className="dark:text-gray-400 ckbo4"
                                d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z"
                                fill="currentColor"
                            />
                            <path
                                className="cmpw7 cdqku"
                                d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z"
                                fill="currentColor"
                            />
                        </svg>
                    </div>
                    <h4 className="text-gray-800 dark:text-gray-100 font-bold text-lg c6f83">
                    Données indisponibles
                    </h4>
                    <div className="mb-6 text-sm">
                    {error.message}
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                        setLoading(true);
                        fetchAffluence({ silent: false });
                        }}
                        className="inline-flex items-center justify-center rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2 text-sm"
                    >
                        Réessayer
                    </button>
                </div>
            </div>
      
    );
  }

  return (
    <div className="relative min-h-full overflow-hidden">
      {/* Fond atmosphère */}
      <div
        className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(45,212,191,0.18),transparent_50%),radial-gradient(ellipse_80%_50%_at_100%_50%,rgba(251,191,36,0.08),transparent_45%)] dark:bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(20,184,166,0.12),transparent_50%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none fixed inset-0 -z-10 opacity-[0.35] dark:opacity-20 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%2394a3b8%22 fill-opacity=%220.12%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"
        aria-hidden
      />

      <div className="relative px-4 sm:px-6 lg:px-10 py-8 lg:py-10 w-full max-w-[1600px] mx-auto">
        {/* Hero */}
        <header className="relative mb-10 lg:mb-12 overflow-hidden rounded-3xl border border-white/20 bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-950 shadow-2xl shadow-emerald-950/40">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.03%22 fill-rule=%22evenodd%22%3E%3Cpath d=%22M0 40L40 0H20L0 20M40 40V20L20 40%22/%3E%3C/g%3E%3C/svg%3E')] opacity-90" />
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-teal-400/10 blur-3xl" />

          <div className="relative px-6 py-8 sm:px-10 sm:py-10 lg:flex lg:items-center lg:justify-between gap-8">
            <div className="max-w-xl mb-8 lg:mb-0">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-emerald-200 ring-1 ring-white/20">
                  <span className="relative flex h-2 w-2">
                    <span
                      className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 ${liveRefresh ? "animate-ping" : ""}`}
                    />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  </span>
                  {liveRefresh ? "Flux temps réel" : "Mise à jour manuelle"}
                </span>
                {/* {lastUpdated && (
                  <span className="text-xs text-white/50">
                    MAJ {lastUpdated.toLocaleTimeString("fr-FR")}
                  </span>
                )} */}
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight leading-tight">
                Affluence &{" "}
                <span className="bg-gradient-to-r from-emerald-300 to-teal-200 bg-clip-text text-transparent">
                  contrôle d&apos;entrée
                </span>
              </h1>
              <p className="mt-3 text-sm sm:text-base text-white/70 leading-relaxed">
                Visualisez en un coup d&apos;œil les scans{" "}
                <strong className="text-white/90">physiques</strong> et{" "}
                <strong className="text-white/90">numériques</strong>, avec une
                lecture claire des entrées validées et des billets encore à
                contrôler.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
              <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-white/10 px-4 py-3 text-sm font-medium text-white ring-1 ring-white/20 backdrop-blur-sm hover:bg-white/15 transition-colors">
                <input
                  type="checkbox"
                  className="rounded border-white/30 bg-white/10 text-emerald-500 focus:ring-emerald-400 focus:ring-offset-0"
                  checked={liveRefresh}
                  onChange={(e) => setLiveRefresh(e.target.checked)}
                />
                Auto 20s
              </label>
              <button
                type="button"
                disabled={refreshing}
                onClick={() => fetchAffluence({ silent: true })}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-slate-900 shadow-lg shadow-black/20 hover:bg-emerald-50 disabled:opacity-60 transition-colors"
              >
                <svg
                  className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Actualiser
              </button>
            </div>
          </div>
        </header>

        {/* Bento KPI */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 mb-10 lg:mb-12">
          {/* Carte principale : taux + anneau */}
          <div className="lg:col-span-5 rounded-3xl border border-gray-200/80 dark:border-gray-700/80 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl p-6 sm:p-8 shadow-xl shadow-gray-900/5 dark:shadow-black/40 flex flex-col justify-between min-h-[280px]">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Performance globale
              </p>
              <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                Taux de scan
              </p>
            </div>
            <div className="flex justify-center py-4">
              <RateRing
                value={globalTotalTickets ? globalRate : 0}
                label="entrées"
                sub={
                  globalTotalTickets
                    ? `${formatNumber(globalScanned)} entrées validées sur ${formatNumber(globalTotalTickets)} billets émis.`
                    : "Les indicateurs s’afficheront dès réception des données."
                }
              />
            </div>
            {/* <div className="flex h-2.5 w-full rounded-full overflow-hidden ring-1 ring-inset ring-gray-200 dark:ring-gray-600">
              {globalTotalTickets > 0 ? (
                <>
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 shrink-0 transition-[width] duration-700"
                    style={{ width: `${Math.min(100, globalRate)}%` }}
                  />
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-400 shrink-0 transition-[width] duration-700"
                    style={{
                      width: `${Math.max(0, 100 - Math.min(100, globalRate))}%`,
                    }}
                  />
                </>
              ) : (
                <div className="h-full w-full bg-gray-200 dark:bg-gray-700" />
              )}
            </div> */}
          </div>

          {/* Grille secondaire */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
            <div className="rounded-3xl border border-gray-200/80 dark:border-gray-700/80 bg-gradient-to-br from-white to-slate-50 dark:from-gray-900 dark:to-gray-950 p-6 shadow-lg shadow-gray-900/5 dark:shadow-black/30 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-slate-200/50 to-transparent dark:from-white/5 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110" />
              <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 relative">
                Volume total
              </p>
              <p className="mt-3 text-4xl font-black text-gray-900 dark:text-white tabular-nums tracking-tight relative">
                {formatNumber(globalTotalTickets)}
              </p>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 relative">
                Billets physiques + numériques
              </p>
            </div>

            <div className="rounded-3xl border border-emerald-200/60 dark:border-emerald-900/50 bg-gradient-to-br from-emerald-50/90 to-teal-50/50 dark:from-emerald-950/40 dark:to-gray-900 p-6 shadow-lg shadow-emerald-900/10 relative overflow-hidden">
              <p className="text-xs font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-400">
                Entrées validées
              </p>
              <p className="mt-3 text-4xl font-black bg-gradient-to-r from-emerald-600 to-teal-600 dark:from-emerald-400 dark:to-teal-400 bg-clip-text text-transparent tabular-nums">
                {formatNumber(globalScanned)}
              </p>
              <p className="mt-2 text-sm text-emerald-800/80 dark:text-emerald-300/80">
                Scans confirmés à l&apos;entrée
              </p>
            </div>

            <div className="sm:col-span-2 rounded-3xl border border-amber-200/60 dark:border-amber-900/40 bg-gradient-to-r from-amber-50 via-orange-50/80 to-rose-50/50 dark:from-amber-950/30 dark:via-gray-900 dark:to-rose-950/20 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-amber-800 dark:text-amber-400">
                  En file d&apos;attente
                </p>
                <p className="mt-2 text-3xl sm:text-4xl font-black text-amber-900 dark:text-amber-200 tabular-nums">
                  {formatNumber(globalPending)}
                </p>
                <p className="mt-1 text-sm text-amber-900/70 dark:text-amber-300/70">
                  Billets encore non scannés (sur le total global)
                </p>
              </div>
              <div className="flex gap-8 text-center sm:text-right shrink-0">
                <div>
                  <p className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400">
                    Physique
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">
                    {formatNumber(totals.physicalScanned)}/
                    {formatNumber(totals.physicalTotal)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400">
                    Numérique
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">
                    {formatNumber(totals.digitalScanned)}/
                    {formatNumber(totals.digitalTotal)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Liste événements */}
        {rows.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-gray-300/80 dark:border-gray-600 bg-white/50 dark:bg-gray-900/40 backdrop-blur-sm px-8 py-16 text-center">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-200 to-slate-100 dark:from-gray-800 dark:to-gray-900 mb-4">
              <svg
                className="w-8 h-8 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <p className="text-gray-800 dark:text-gray-200 font-semibold text-lg">
              Aucune donnée d&apos;affluence pour l&apos;instant
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-md mx-auto">
              Dès que vos statistiques par événement seront disponibles, elles
              apparaîtront ici avec les meilleurs niveau de détail.
            </p>
          </div>
        ) : (
          <section>
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                  Par événement
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Détail physique / numérique pour chaque manifestation
                </p>
              </div>
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider hidden sm:block">
                {rows.length} événement{rows.length > 1 ? "s" : ""}
              </span>
            </div>

            <ul className="space-y-8">
              {rows.map((ev, index) => {
                const evTotal = ev.physical.total + ev.digital.total;
                const evScanned = ev.physical.scanned + ev.digital.scanned;
                const evRate = pct(evScanned, evTotal);

                return (
                  <li
                    key={ev.id || `${ev.event_code}-${index}`}
                    className="animate-affluence-card"
                    style={{ animationDelay: `${index * 70}ms` }}
                  >
                    <article className="group relative overflow-hidden rounded-3xl border border-gray-200/90 dark:border-gray-700/90 bg-white dark:bg-gray-900 shadow-xl shadow-gray-900/5 dark:shadow-black/50 transition-all duration-300 hover:shadow-2xl">
                      {/* Bandeau visuel */}
                      <div className="relative lg:flex">
                        <div className="relative lg:w-[min(280px,32%)] shrink-0 aspect-[16/10] lg:aspect-auto lg:min-h-[220px] overflow-hidden">
                          {ev.affiche ? (
                            <img
                              src={`data:image/jpeg;base64,${ev.affiche}`}
                              alt=""
                              className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                          ) : (
                            <div className="absolute inset-0 bg-gradient-to-br via-teal-700 to-slate-900 flex items-center justify-center">
                              <span className="text-5xl font-black text-gray-500/30">
                                {(ev.event_title || "?").slice(0, 1).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent lg:bg-gradient-to-r lg:from-black/60 lg:via-black/25 lg:to-transparent" />
                          <div className="absolute bottom-4 left-4 right-4 lg:bottom-auto lg:top-1/2 lg:-translate-y-1/2 lg:left-6 lg:right-6">
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white ring-1 ring-white/30 backdrop-blur-md">
                              #{ev.event_code}
                            </span>
                            {ev.default_date && (
                              <p className="mt-2 text-xs text-white/85 font-medium">
                                {ev.default_date}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex-1 p-6 sm:p-8 flex flex-col justify-center">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                            <div className="min-w-0">
                              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight leading-snug transition-colors">
                                {ev.event_title}
                              </h3>
                              <div className="mt-3 flex flex-wrap items-center gap-3">
                                <span className="inline-flex items-baseline gap-2 rounded-2xl bg-gray-100 dark:bg-gray-800 px-4 py-2 ring-1 ring-gray-200/80 dark:ring-gray-700">
                                  <span className="text-2xl font-black tabular-nums text-gray-900 dark:text-white">
                                    {formatNumber(evScanned)}
                                  </span>
                                  <span className="text-sm text-gray-500 dark:text-gray-400">
                                    / {formatNumber(evTotal)} entrées
                                  </span>
                                </span>
                                {evTotal > 0 && (
                                  <span className="inline-flex items-center rounded-full bg-emerald-100 dark:bg-emerald-900/50 px-3 py-1 text-sm font-bold text-emerald-800 dark:text-emerald-300 ring-1 ring-emerald-200/80 dark:ring-emerald-800">
                                    {evRate}% complété
                                  </span>
                                )}
                              </div>
                            </div>
                            <Link
                              to={
                                ev.id
                                  ? `/dashboard/events/view-details/to/${ev.id}`
                                  : "#"
                              }
                              className={`inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 dark:bg-white px-4 py-1.5 text-xs font-bold text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-emerald-100 transition-colors shrink-0 ${!ev.id ? "pointer-events-none opacity-50" : ""}`}
                            >
                              Fiche événement
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                                />
                              </svg>
                            </Link>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <ChannelBlock
                              title="Billets physiques"
                              subtitle="Imprimés & supports papier"
                              variant="physical"
                              icon={
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                                  />
                                </svg>
                              }
                              data={ev.physical}
                            />
                            <ChannelBlock
                              title="Billets numériques"
                              subtitle="QR mobile & e-billets"
                              variant="digital"
                              icon={
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                                  />
                                </svg>
                              }
                              data={ev.digital}
                            />
                          </div>
                        </div>
                      </div>
                    </article>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
